/**
 * Are the chart's files still in this browser's storage? (W3-07; UX-065 / UX-066 / UX-067)
 *
 * A file of a chart can be listed in the document while its bytes are gone from the browser's storage (the
 * browser dropped them). `persist.hasAsset` is the presence probe (a record whose bytes are missing counts as
 * absent). This module keeps what the probes found per persistence client, so the Assets panel can mark such a file
 * "Missing from this browser" with "Choose file...", and watches the open chart: when a chart opens, every file it
 * lists is probed once and a missing one is announced with a toast whose action chooses the file again.
 *
 * Putting the same file again (Replace, Add) writes the bytes back; `markPresent` records that at once.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { AssetId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import type { PersistClient } from '../../persist/types';
import type { DocStore } from '../../state/types';
import { m } from './assets.messages';

/** Marker of a persist rejection whose stored bytes are gone (persist/assetStore.ts `ASSET_BYTES_MISSING`). */
export const ASSET_BYTES_MISSING = 'asset bytes missing';

/** True when a persist rejection says that stored file bytes are missing (the record is still there). */
export function isBytesMissing(err: unknown): boolean {
  const msg =
    typeof err === 'object' && err !== null && typeof (err as { message?: unknown }).message === 'string'
      ? (err as { message: string }).message
      : typeof err === 'string'
        ? err
        : '';
  return msg.includes(ASSET_BYTES_MISSING);
}

interface PresenceState {
  /** true: bytes present; false: missing; absent: not probed yet. */
  readonly known: Map<AssetId, boolean>;
  readonly inflight: Map<AssetId, Promise<boolean>>;
  readonly listeners: Set<() => void>;
  version: number;
}

const states = new WeakMap<object, PresenceState>();

function stateOf(persist: object): PresenceState {
  let st = states.get(persist);
  if (st === undefined) {
    st = { known: new Map(), inflight: new Map(), listeners: new Set(), version: 0 };
    states.set(persist, st);
  }
  return st;
}

function set(st: PresenceState, id: AssetId, present: boolean): void {
  if (st.known.get(id) === present) return;
  st.known.set(id, present);
  st.version++;
  for (const cb of [...st.listeners]) cb();
}

/** Probes one file (deduplicated while a probe is running). A failed probe counts as present (no false alarm). */
export function probeAsset(persist: Pick<PersistClient, 'hasAsset'>, id: AssetId): Promise<boolean> {
  const st = stateOf(persist);
  const running = st.inflight.get(id);
  if (running !== undefined) return running;
  const p = persist.hasAsset(id).then(
    (present) => present,
    () => true,
  );
  st.inflight.set(id, p);
  void p.then((present) => {
    st.inflight.delete(id);
    set(st, id, present);
  });
  return p;
}

/** Probes files and resolves with the missing ones. */
export async function probeAssets(
  persist: Pick<PersistClient, 'hasAsset'>,
  ids: Iterable<AssetId>,
): Promise<AssetId[]> {
  const list = [...new Set(ids)];
  const present = await Promise.all(list.map((id) => probeAsset(persist, id)));
  return list.filter((_, i) => present[i] === false);
}

/** The bytes were just stored (a put of the file succeeded). */
export function markPresent(persist: object, id: AssetId): void {
  set(stateOf(persist), id, true);
}

/** What is known about a file: true present, false missing, null not probed. */
export function knownPresence(persist: object, id: AssetId): boolean | null {
  return stateOf(persist).known.get(id) ?? null;
}

/** Missing files among `ids` (re-renders when a probe or put changes what is known; probes unknown ids once). */
export function useMissingAssets(
  persist: Pick<PersistClient, 'hasAsset'>,
  ids: readonly AssetId[],
): ReadonlySet<AssetId> {
  const st = stateOf(persist);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => {
      st.listeners.add(cb);
      return () => {
        st.listeners.delete(cb);
      };
    },
    [st],
  );
  const key = ids.join(',');
  // The missing ids as one string: a stable snapshot that changes only when the answer does.
  const missingOf = (): string =>
    (key === '' ? [] : key.split(',')).filter((id) => st.known.get(id as AssetId) === false).join(',');
  const missingKey = useSyncExternalStore(subscribe, missingOf, missingOf);
  useEffect(() => {
    for (const id of key === '' ? [] : (key.split(',') as AssetId[])) {
      if (!st.known.has(id)) void probeAsset(persist, id);
    }
  }, [persist, st, key]);
  return useMemo(() => new Set(missingKey === '' ? [] : (missingKey.split(',') as AssetId[])), [missingKey]);
}

/**
 * When a chart opens, probes every file it lists and announces missing ones (one toast: "Choose file..." for one
 * file, "Show files" for several). The Assets module's `init`; returns the disposer.
 */
export function watchAssetPresence(services: Pick<AppServices, 'store' | 'onChange' | 'persist' | 'ui'>): Unsubscribe {
  let store: DocStore | null = null;
  let disposed = false;
  const check = (watched: DocStore): void => {
    const ids = [...watched.doc.assets.values()].filter((a) => a.missing !== true).map((a) => a.id);
    if (ids.length === 0) return;
    void probeAssets(services.persist, ids).then((missing) => {
      if (disposed || services.store !== watched || missing.length === 0) return;
      const names = missing.map((id) => watched.doc.assets.get(id)?.path ?? '').filter((p) => p !== '');
      const first = missing[0];
      if (missing.length === 1 && first !== undefined) {
        services.ui.toast(
          { key: m.storageMissingOne, params: { name: names[0] ?? '' } },
          {
            kind: 'error',
            durationMs: 20_000,
            action: { label: { key: m.chooseFile }, command: 'assets.replace', args: { asset: first } },
          },
        );
        return;
      }
      services.ui.toast(
        { key: m.storageMissingMany, params: { count: missing.length, names: names.join(', ') } },
        { kind: 'error', durationMs: 20_000, action: { label: { key: m.showFiles }, command: 'assets.show' } },
      );
    });
  };
  const attach = (): void => {
    if (services.store === store) return;
    store = services.store;
    if (store !== null) check(store);
  };
  attach();
  const off = services.onChange(attach);
  return () => {
    disposed = true;
    off();
  };
}
